import { isClient } from "./isClient.ts";

type GlobalStore<Value> = {
  get(): Value | undefined;
  set(value: Value): void;
};

const requestStoreKey = "__solvariRequestStore__";

function getRequestStore(): Map<string, unknown> {
  const getter = (globalThis as Record<string, unknown>)[requestStoreKey] as
    | (() => Map<string, unknown>)
    | undefined;
  if (!getter) {
    throw new Error(
      "No request store registered. Ensure the asyncLocalStorage plugin is loaded and experimental.asyncContext is enabled.",
    );
  }
  return getter();
}

/**
 * Returns a keyed store scoped to the current environment.
 *
 * - **Client**: backed by `globalThis` so multiple builds on the same page share state.
 * - **Server**: backed by Nuxt's async context so concurrent requests stay isolated.
 *
 * @example
 * useGlobal<SolvariEnv>("solvari-env").set(env);
 * useGlobal<SolvariEnv>("solvari-env").get();
 */
function useGlobal<Value>(key: string): GlobalStore<Value> {
  if (isClient()) {
    return {
      get: () =>
        (globalThis as Record<string, unknown>)[key] as Value | undefined,
      set: (value) => {
        (globalThis as Record<string, unknown>)[key] = value;
      },
    };
  }

  return {
    get: () => getRequestStore().get(key) as Value | undefined,
    set: (value) => {
      getRequestStore().set(key, value);
    },
  };
}

export { requestStoreKey, useGlobal };
export type { GlobalStore };
